import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Trash2, Plus, Globe, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const DEFAULT_DESCRIPTION = 'Rede da empresa';

const errorMessage = (error, fallback) => error?.response?.data?.detail || fallback;

export default function IpBlockTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  const apply = useCallback((next) => {
    setData(next);
    return next;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/access-control');
        const next = apply(response.data);
        // Deixa o campo já preenchido com o IP atual quando ele ainda não está cadastrado
        if (next.current_ip && !next.current_ip_registered) {
          setAddress(next.current_ip);
          setDescription(DEFAULT_DESCRIPTION);
        }
        setPrefilled(true);
      } catch (error) {
        toast.error(errorMessage(error, 'Não foi possível carregar o bloqueio por IP'));
      } finally {
        setLoading(false);
      }
    })();
  }, [api, apply]);

  const run = async (request, successMessage) => {
    setSaving(true);
    try {
      const response = await request();
      apply(response.data);
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(errorMessage(error, 'Não foi possível concluir a operação'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => run(
    () => api.put('/access-control/settings', { ip_restriction_enabled: !data.ip_restriction_enabled }),
    data.ip_restriction_enabled ? 'Bloqueio por IP desativado' : 'Bloqueio por IP ativado'
  );

  const add = async (ip, desc) => {
    if (!ip.trim()) {
      toast.error('Informe um IP, uma faixa ou um endereço DDNS');
      return;
    }
    const ok = await run(
      () => api.post('/access-control/ips', { ip_address: ip.trim(), description: desc.trim() || null }),
      'Endereço cadastrado'
    );
    if (ok) {
      setAddress('');
      setDescription('');
    }
  };

  const remove = (entry) => {
    if (!window.confirm(`Remover ${entry.ip_address} da lista de acesso?`)) return;
    run(() => api.delete(`/access-control/ips/${entry.id}`), 'Endereço removido');
  };

  if (loading) {
    return <div className="py-10 text-center text-sm text-slate-500">Carregando...</div>;
  }
  if (!data) {
    return <div className="py-10 text-center text-sm text-slate-500">Não foi possível carregar o bloqueio por IP.</div>;
  }

  const enabled = data.ip_restriction_enabled;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              {enabled ? <ShieldCheck className="h-5 w-5 text-green-600" /> : <ShieldOff className="h-5 w-5 text-slate-400" />}
              Bloqueio por IP
              <Badge className={enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-700'}>
                {enabled ? 'Bloqueio ATIVO' : 'Bloqueio desativado'}
              </Badge>
            </CardTitle>
            <Button
              onClick={toggle}
              disabled={saving}
              variant={enabled ? 'outline' : 'default'}
              className={enabled ? '' : 'bg-blue-600 hover:bg-blue-700 text-white'}
            >
              {enabled ? 'Desativar bloqueio' : 'Ativar bloqueio'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-600">
          <p>
            Com o bloqueio ativo, o sistema só abre a partir dos endereços cadastrados abaixo.
            Quem estiver em outra rede (home office, 4G) não consegue entrar. Desativado, o acesso é liberado de qualquer lugar.
          </p>
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <Globe className="h-4 w-4 text-slate-500" />
            <span>
              Seu IP atual: <strong className="font-mono">{data.current_ip || 'não identificado'}</strong>
            </span>
            {data.current_ip_registered ? (
              <Badge className="bg-green-100 text-green-800">IP desta rede já cadastrado</Badge>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={saving || !data.current_ip}
                onClick={() => add(data.current_ip, DEFAULT_DESCRIPTION)}
              >
                Cadastrar o IP desta rede
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Este é o IP que o servidor enxerga, e é ele que vale para o bloqueio.
            Cadastre o IP da empresa antes de ativar: o sistema recusa ativar (ou remover um endereço) se o seu IP ficasse sem acesso.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Adicionar IP, faixa ou DDNS</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <form
            className="grid gap-3 md:grid-cols-[2fr_2fr_auto] md:items-end"
            onSubmit={(e) => { e.preventDefault(); add(address, description); }}
          >
            <div className="space-y-1">
              <Label htmlFor="ip-address">IP, faixa ou DDNS</Label>
              <Input
                id="ip-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="200.150.10.5, 192.168.1.0/24 ou minhaempresa.ddns.net"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ip-description">Descrição (opcional)</Label>
              <Input
                id="ip-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={DEFAULT_DESCRIPTION}
                maxLength={200}
              />
            </div>
            <Button type="submit" disabled={saving || !prefilled} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="mr-1 h-4 w-4" /> Adicionar
            </Button>
          </form>
          <p className="text-xs text-slate-500">
            Sem IP fixo? Use um endereço DDNS (serviço gratuito que mantém um nome, como minhaempresa.ddns.net,
            sempre apontando para o IP atual da sua internet; configure-o no roteador). O sistema consulta o
            nome periodicamente e libera o IP que ele indicar.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Endereços autorizados ({data.allowed_ips.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {data.allowed_ips.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum endereço cadastrado.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.allowed_ips.map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-medium">{entry.ip_address}</span>
                      {entry.is_hostname && <Badge className="bg-blue-100 text-blue-800">DDNS</Badge>}
                    </div>
                    {entry.description && <p className="text-xs text-slate-500">{entry.description}</p>}
                    {entry.is_hostname && (
                      entry.resolved_ips.length > 0 ? (
                        <p className="text-xs text-slate-600">IP atual: <span className="font-mono">{entry.resolved_ips.join(', ')}</span></p>
                      ) : (
                        <p className="flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="h-3 w-3" /> Não foi possível resolver este endereço agora; ninguém é liberado por ele.
                        </p>
                      )
                    )}
                  </div>
                  <Button size="sm" variant="outline" disabled={saving} onClick={() => remove(entry)}>
                    <Trash2 className="mr-1 h-4 w-4" /> Remover
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
